export function formatCurrency(value: number | undefined | null, currency = 'EUR'): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(value);
  } catch {
    return `${value.toFixed(2)} €`;
  }
}

// Same as formatCurrency but expands to 4 decimals for non-zero amounts under
// one cent — used on container pricing where a stack running for a few minutes
// might cost €0.0046 and we don't want that to display as "€0,00".
export function formatCurrencyPrecise(value: number | undefined | null, currency = 'EUR'): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  const fractionDigits = abs > 0 && abs < 0.01 ? 4 : 2;
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  } catch {
    return `${value.toFixed(fractionDigits)} €`;
  }
}

// Single locale for *all* dates across the dashboard. en-GB produces
// "13 Feb 2026" (medium) and "15:40" (short time) — day-first, abbreviated
// month, 24h clock. Do not switch this to user-browser locale: it must stay
// consistent regardless of where the user is.
const DATE_LOCALE = 'en-GB';

export function formatDate(value: string | Date | undefined | null, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(DATE_LOCALE, opts).format(d);
  } catch {
    return '—';
  }
}

export function formatDateTime(value: string | Date | undefined | null): string {
  return formatDate(value, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatTime(value: string | Date | undefined | null): string {
  return formatDate(value, { timeStyle: 'short' });
}

export function formatBytes(bytes: number | undefined | null, decimals = 1): string {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const idx = Math.min(i, sizes.length - 1);
  return `${(bytes / Math.pow(k, idx)).toFixed(decimals)} ${sizes[idx]}`;
}

export function relativeTime(value: string | Date | undefined | null): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const diff = (d.getTime() - Date.now()) / 1000;
  const absDiff = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (absDiff < 60) return rtf.format(Math.round(diff), 'second');
  if (absDiff < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (absDiff < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (absDiff < 2592000) return rtf.format(Math.round(diff / 86400), 'day');
  if (absDiff < 31536000) return rtf.format(Math.round(diff / 2592000), 'month');
  return rtf.format(Math.round(diff / 31536000), 'year');
}

export function truncateMiddle(value: string | undefined | null, max = 24): string {
  if (!value) return '';
  if (value.length <= max) return value;
  const half = Math.floor((max - 1) / 2);
  return `${value.slice(0, half)}…${value.slice(-half)}`;
}
